import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { CacheService } from 'projects/common/src/app/services/cache.service';
import { ParameterHandlerService } from 'projects/common/src/app/services/parameter-handler.service';
import { LanguageService } from 'projects/common/src/app/services/parameters/language/language.service';
import { IShift } from 'src/app/modules/core/interfaces';
import { InputDateFormatPipe } from 'src/app/modules/core/pipes/input-date-format.pipe';
import { BooleanStateService } from 'src/app/modules/core/services/boolean-state.service';
import { MachineContextService } from 'src/app/modules/core/services/parameter/machine-context/machine-context.service';
import { DashboardFacade } from 'src/app/modules/core/store/dashboard/dashboard.facade';
import { StateSidebarService } from 'src/app/modules/sidebar/services/state-sidebar.service';
import { IModalInputs } from 'src/app/modules/widget/interfaces/production-type-truck-point';
import { ScreenViewService } from 'src/app/modules/widget/services/screen-view.service';
import { CommunicationService } from 'src/app/services/communication/communication.service';
import { ProductionByStreetChartComponent } from '../production-by-street/production-by-street-chart/production-by-street-chart.component';
import { ProdByTypetruckModalComponent } from './prod-by-typetruck-modal/prod-by-typetruck-modal.component';

@Component({
  selector: 'app-production-by-typetruck-dump-point',
  templateUrl: './production-by-typetruck-dump-point.component.html',
})
export class ProductionByTypetruckDumpPointComponent extends ProductionByStreetChartComponent implements OnInit, AfterViewInit, OnDestroy {
  public loopByModel: [];
  public hasData = false;
  constructor(
    public dashboardFacade: DashboardFacade,
    protected progressBarService: BooleanStateService,
    protected communication: CommunicationService,
    protected parameterService: ParameterHandlerService,
    protected sidebarService: StateSidebarService,
    protected cacheService: CacheService,
    protected machineContext: MachineContextService,
    protected langService: LanguageService,
    protected modalService: NgbModal,
    protected inputDatePipe: InputDateFormatPipe,
    protected screenViewService: ScreenViewService,
  ) {
    super(
      dashboardFacade,
      progressBarService,
      communication,
      parameterService,
      sidebarService,
      cacheService,
      machineContext,
      langService,
      modalService,
      inputDatePipe,
      screenViewService,
    );
    this.initRequestParams();
  }

  /**
   * Init request params
   */
  protected initRequestParams(): void {
    this.params = {
      hasDate: true,
      hasShift: true,
      hasCompany: true,
      hasZone: true,
    };
  }

  /**
   * On init component
   */
  public ngOnInit(): void {
    super.ngOnInit();
    this.isLhd = false;
    this.isTruck = true;
  }

  /**
   * Function to draw chart
   *
   * @param chartOptions ChartOptions
   */
  public async drawView(chartOptions: any): Promise<void> {
    this.date = this.parameterService.parameters.item('date').value;
    const shift = this.parameterService.parameters.item('shift').value as IShift;
    this.typeActive = true;
    this.dataProd = chartOptions;
    if (this.dataProd) {
      this.setChartConfig(shift, this.dataProd, this.typeActive);
      this.hasData = true;
    } else {
      this.hasData = false;
    }
  }

  /**
   * Function to build series for display chart values
   *
   * @param prodByStreetList data to fill chart view
   */
  protected buildSeriesForChart(prodByTypeTruck: any): any {
    let series = [];
    for (const model of prodByTypeTruck.lapsMachineModel) {
      const data = prodByTypeTruck.totalLapsList.map((lap) => {
        const mapped = lap.lapsByMachineModel.find((total) => total.machineModelId === model.machineModelId);

        return { id: lap.loopId, y: mapped.totalLaps };
      });

      const serie = {
        name: model.name,
        data,
        color: `${model.colorCode}80`,
        borderColor: model.colorCode,
        borderWidth: 2,
        states: {
          hover: {
            color: model.colorCode,
          },
        },
      };
      series = [...series, serie];

      const allZeros = series.every((item) => item.data.every((point) => point.y === 0));

      if (allZeros) {
        this.chartWithoutData$.next(true);
      } else this.chartWithoutData$.next(false);
    }
    return series;
  }

  /**
   * open modal
   *
   * @param inputs data to open modal
   */
  protected openModal(inputs: IModalInputs): void {
    const modalRef = inputs.modalSvc.open(ProdByTypetruckModalComponent, { centered: true, windowClass: 'prod-typetruck-modal-width' });
    modalRef.componentInstance.modalSubtitle = inputs.shiftName;
    modalRef.componentInstance.shift = inputs.shift;
    modalRef.componentInstance.streetId = inputs.id;
    modalRef.componentInstance.streetName = inputs.category;
    modalRef.componentInstance.date = inputs.date;
    modalRef.componentInstance.shiftLabel = inputs.shiftName;
    modalRef.componentInstance.zoneName = inputs.zoneName;
  }

  /**
   * Define chart config properties
   *
   * @param shift shift object to build labels
   * @param dataProdList production list to build categories for char
   */
  protected defineChartProperties(shift: IShift, dataProdList: any): any {
    const shiftName = this.setShiftName(shift);
    const categories = dataProdList.totalLapsList.map((dataTruck) => dataTruck.loopName);
    const xtext = this.langService.message('loop-y').toUpperCase();
    const ytext = this.langService.message('loops').toUpperCase();
    const series = this.buildSeriesForChart(dataProdList);
    return { categories, xtext, ytext, shiftName, series };
  }

  /**
   * Build html tooltip for chart
   *
   * @param tooltipProps properties to build tooltip
   */
  protected defineTooltipHtml = (tooltipProps: any): string => {
    const unitTooltip = this.langService.message('loop');
    return `
      <div style="width: 150px;">
        <strong>${tooltipProps.shiftName.toUpperCase()}</strong> <br>
        <span>${tooltipProps.key}</span> <br>
        <div style="margin-top: 2px;">
          <svg width="20" height="13" style="float: left; margin-right: 5px; border-radius: 1px;">
            <rect width="20" height="13" style="fill:${tooltipProps.color.slice(0, -2)};"/>
          </svg>
          <span>${tooltipProps.y} ${unitTooltip}${tooltipProps.y === 0 || tooltipProps.y > 1 ? 's' : ''} </span>
      </div>`;
  };
}
